import { EditOutlined } from "@ant-design/icons"
import { Button, Flex, Tooltip, Typography } from "antd"
import React from "react"
import { useTranslation } from "react-i18next"

import { LeadPill } from "../../../Leads/components/LeadPill.tsx"
import { NeedsInfoDot } from "../components/LeadWorkspaceHeader/NeedsInfoDot.tsx"
import { useNeedsInfoEditor } from "../components/LeadWorkspaceHeader/needs-info-editor-context.tsx"
import { needsInfoCategoryLabel } from "../../constants/needs-info.ts"
import { OPERATIONAL_STATES } from "../../constants/operational-states.ts"
import { type Lead } from "../../types/lead.types.ts"
import { OVERVIEW_ACCENTS } from "./OverviewCardIcon.tsx"

const { Text } = Typography

const categoryChipStyle = {
  color: OVERVIEW_ACCENTS.orange.color,
  backgroundColor: OVERVIEW_ACCENTS.orange.background
}

function SegmentDivider() {
  return <div className={"h-4 w-px shrink-0 bg-gray-200"} aria-hidden />
}

interface LeadOverviewNeedsInfoSectionProps {
  lead: Lead
  canEdit?: boolean
}

/** Read-only summary of what Needs Info is waiting on; the header owns the status itself. */
function LeadOverviewNeedsInfoSection({
  lead,
  canEdit
}: LeadOverviewNeedsInfoSectionProps) {
  const { t } = useTranslation()
  const { openNeedsInfoEditor } = useNeedsInfoEditor()

  const isNeedsInfo = (lead.operational_states ?? []).includes(
    OPERATIONAL_STATES.NEEDS_INFO
  )
  const items = lead.needs_info_items ?? []
  if (!isNeedsInfo || items.length === 0) return null

  const editLabel = t("leadWorkspace.header.states.needsInfo.editLink")

  return (
    <div
      // w-fit opts out of the parent flex column's stretch so the note is content-sized.
      className={
        "w-fit max-w-[900px] rounded-card border border-border px-3 py-3.5"
      }
      data-testid={"lead-overview-needs-info"}
    >
      <Flex align={"center"} gap={10} wrap>
        <Flex align={"center"} gap={6} className={"shrink-0"}>
          <NeedsInfoDot className={"m-0"} />
          <Text strong className={"text-xs whitespace-nowrap"}>
            {t("leadWorkspace.header.states.needsInfo.overviewTitle")}
          </Text>
          <Text type={"secondary"} className={"text-xs whitespace-nowrap"}>
            ·{" "}
            {t("leadWorkspace.header.states.needsInfo.itemCount", {
              count: items.length
            })}
          </Text>
        </Flex>

        {items.map((item) => (
          <Flex
            key={item.category}
            align={"center"}
            gap={6}
            className={"shrink-0"}
          >
            <SegmentDivider />
            <LeadPill style={categoryChipStyle} className={"shrink-0"}>
              {needsInfoCategoryLabel(t, item.category)}
            </LeadPill>
            {item.description && (
              <Text className={"text-xs"}>{item.description}</Text>
            )}
          </Flex>
        ))}

        {canEdit && (
          <>
            <SegmentDivider />
            <Tooltip title={editLabel}>
              <Button
                size={"small"}
                type={"text"}
                icon={<EditOutlined />}
                aria-label={editLabel}
                className={"shrink-0"}
                onClick={openNeedsInfoEditor}
              />
            </Tooltip>
          </>
        )}
      </Flex>
    </div>
  )
}

export { LeadOverviewNeedsInfoSection }
